<!-- src/components/device/AccessoriesPanel.vue -->
<!--
  Les accessoires radio jumelés à un appareil : les nommer, les retirer.

  Troisième composant partagé entre les deux espaces, après `DeviceSchedule` et
  `ContactsPanel`, et pour la même raison : l'écran client ne savait qu'AFFICHER la liste
  alors que le backend l'autorisait déjà à écrire. Sans renommage, un client vit avec
  « Accessoire #14532 » et ne sait pas quelle porte c'est — ce qui rend l'historique des
  alarmes illisible au moment où il compte.

  Deux réglages distinguent les deux espaces :
    `technique`   — l'adresse radio et le code réseau, identifiants de dépannage que le
                    client n'a aucun moyen d'exploiter et qui encombrent sa liste ;
    `toutRetirer` — le retrait global. Le client l'a déjà dans son onglet Commandes ; le
                    dupliquer ici lui donnerait deux boutons pour le même geste.
-->
<template>
  <div class="bg-white rounded-lg shadow overflow-hidden">
    <div class="px-4 py-3 border-b border-gray-200 flex justify-between items-center gap-3">
      <h3 class="font-semibold text-gray-800">
        {{ $t('accessoires.titre') }}
        <span class="text-sm font-normal text-gray-400 ml-1">{{ accessoires.length }}</span>
      </h3>
      <div class="flex gap-3 whitespace-nowrap">
        <button @click="rafraichir" :disabled="chargement" class="text-xs text-primary-600 hover:underline">
          {{ chargement ? $t('commun.chargement') : $t('accessoires.rafraichir') }}
        </button>
        <button v-if="modifiable && toutRetirer && accessoires.length" @click="confirmation = { tout: true }"
                class="text-xs text-red-500 hover:underline">
          {{ $t('accessoires.toutRetirer') }}
        </button>
      </div>
    </div>

    <div v-if="erreur" class="px-4 py-2 bg-red-50 text-sm text-red-700">{{ erreur }}</div>

    <div v-if="accessoires.length === 0" class="p-8 text-center text-gray-400">
      {{ $t('accessoires.aucun') }}
    </div>

    <div v-else class="divide-y divide-gray-100">
      <div v-for="acc in accessoires" :key="acc.id"
           class="px-4 py-3 flex justify-between items-center gap-3 hover:bg-gray-50">
        <div class="min-w-0">
          <div class="font-medium text-sm text-gray-900 truncate">
            {{ acc.name || libelleParDefaut(acc) }}
            <!-- Un accessoire jamais nommé se signale : c'est celui qui rendra l'historique
                 des alarmes incompréhensible le jour où il se déclenchera. -->
            <span v-if="!acc.name" class="ml-1 text-xs font-normal text-amber-600">{{ $t('accessoires.aNommer') }}</span>
          </div>
          <div class="text-xs text-gray-400 mt-0.5">
            <span>{{ typeLisible(acc.dev_type) }}</span>
            <template v-if="technique">
              <span v-if="acc.dev_addr !== null && acc.dev_addr !== undefined"> &bull; Addr : {{ acc.dev_addr }}</span>
              <span v-if="acc.net_code"> &bull; Net : {{ acc.net_code }}</span>
            </template>
          </div>
        </div>
        <div class="flex items-center gap-2 whitespace-nowrap">
          <span :class="`px-2 py-0.5 text-xs rounded-full ${acc.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`">
            {{ acc.active ? $t('accessoires.actif') : $t('accessoires.inactif') }}
          </span>
          <template v-if="modifiable">
            <button @click="ouvrirRenommage(acc)" class="text-xs text-primary-600 hover:underline">{{ $t('accessoires.renommer') }}</button>
            <button @click="confirmation = { acc }" class="text-xs text-red-500 hover:underline">{{ $t('accessoires.retirer') }}</button>
          </template>
        </div>
      </div>
    </div>

    <!-- ── Renommage ──────────────────────────────────────────────────────────────────── -->
    <div v-if="renommage" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="renommage = null"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
          <h3 class="text-lg font-medium">{{ $t('accessoires.renommerTitre') }}</h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ $t('accessoires.renommerAide') }}
          </p>
          <input type="text" v-model="nouveauNom" class="form-control mt-3"
                 :placeholder="$t('accessoires.exempleNom')" @keyup.enter="enregistrerNom">
          <div class="mt-4 flex gap-2 justify-end">
            <button @click="renommage = null" class="btn btn-secondary">{{ $t('commun.annuler') }}</button>
            <button @click="enregistrerNom" :disabled="envoi || !nouveauNom.trim()" class="btn btn-primary">
              {{ $t('commun.enregistrer') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Retrait ────────────────────────────────────────────────────────────────────── -->
    <div v-if="confirmation" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="confirmation = null"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-medium">
            {{ confirmation.tout ? $t('accessoires.retirerTous', { nombre: accessoires.length })
                                 : $t('accessoires.retirerUn', { nom: confirmation.acc.name || libelleParDefaut(confirmation.acc) }) }}
          </h3>
          <p class="mt-2 text-sm text-gray-600">
            {{ $t('accessoires.retraitDetail') }}
            <template v-if="confirmation.tout">
              <strong class="text-amber-700">{{ $t('accessoires.tousRejumeler') }}</strong>
            </template>
          </p>
          <div class="mt-5 flex gap-2 justify-end">
            <button @click="confirmation = null" class="btn btn-secondary">{{ $t('commun.annuler') }}</button>
            <button @click="confirmerRetrait" :disabled="envoi"
                    class="btn btn-primary bg-red-600 hover:bg-red-700">
              {{ envoi ? $t('accessoires.retrait') : $t('accessoires.retirer') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import apiService from '@/services/api.service';
import { useI18n } from 'vue-i18n';
import { texteErreur } from '@/i18n/serveur';

// Table du protocole (spec §4) : ce que le client lit à la place d'un nombre.
// Valeurs = clés de traduction (accessoires.types.*), résolues dans setup().
const TYPES = {
  1: 'telecommande', 18: 'mouvement', 19: 'ouverture',
  47: 'choc', 49: 'panneau'
};

export default defineComponent({
  name: 'AccessoriesPanel',

  props: {
    deviceId:    { type: [Number, String], required: true },
    accessories: { type: Array, default: () => [] },
    // Identifiants de dépannage : sans valeur pour un client, encombrants dans sa liste.
    technique:   { type: Boolean, default: false },
    // Le client a déjà « Retirer tous les accessoires » dans son onglet Commandes.
    toutRetirer: { type: Boolean, default: false },
    // Comptes partagés : un membre consulte, seul le titulaire renomme ou retire.
    modifiable:  { type: Boolean, default: true }
  },

  emits: ['maj'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const chargement = ref(false), envoi = ref(false), erreur = ref('');
    const renommage = ref(null), nouveauNom = ref('');
    const confirmation = ref(null);

    const accessoires = computed(() => props.accessories);
    const typeLisible = typ => (TYPES[typ] ? t(`accessoires.types.${TYPES[typ]}`) : t('accessoires.typeInconnu', { type: typ ?? '?' }));
    const libelleParDefaut = acc => t('accessoires.accessoireDefaut', { id: acc.dev_addr ?? acc.id });

    const rafraichir = async () => {
      chargement.value = true; erreur.value = '';
      try {
        const r = await apiService.getDeviceAccessories(props.deviceId);
        emit('maj', r.data);
      } catch (e) {
        erreur.value = texteErreur(e, t('accessoires.rechargementImpossible'));
      } finally {
        chargement.value = false;
      }
    };

    const ouvrirRenommage = (acc) => {
      renommage.value = acc; nouveauNom.value = acc.name || ''; erreur.value = '';
    };

    const enregistrerNom = async () => {
      const nom = nouveauNom.value.trim();
      if (!nom || envoi.value) return;
      envoi.value = true; erreur.value = '';
      try {
        await apiService.updateAccessoryName(props.deviceId, renommage.value.id, nom);
        emit('maj', props.accessories.map(a => (a.id === renommage.value.id ? { ...a, name: nom } : a)));
        renommage.value = null;
      } catch (e) {
        erreur.value = texteErreur(e, t('accessoires.renommageImpossible'));
      } finally {
        envoi.value = false;
      }
    };

    const confirmerRetrait = async () => {
      envoi.value = true; erreur.value = '';
      try {
        if (confirmation.value.tout) {
          await apiService.deleteAllAccessories(props.deviceId);
          emit('maj', []);
        } else {
          const id = confirmation.value.acc.id;
          await apiService.deleteAccessory(props.deviceId, id);
          emit('maj', props.accessories.filter(a => a.id !== id));
        }
        confirmation.value = null;
      } catch (e) {
        erreur.value = texteErreur(e, t('accessoires.retraitImpossible'));
        confirmation.value = null;
      } finally {
        envoi.value = false;
      }
    };

    return { accessoires, chargement, envoi, erreur, renommage, nouveauNom, confirmation,
             typeLisible, libelleParDefaut, rafraichir, ouvrirRenommage, enregistrerNom,
             confirmerRetrait };
  }
});
</script>
